"use client";

import { useState, useEffect } from "react";
import { useLanguage } from "../../hooks/useLanguage";
import { Megaphone } from "lucide-react";
import config from "../../config/config";
import SectionHeader from "./SectionHeader";
import PressReleaseCard from "../cards/PressReleaseCard";
import PostsSkeleton from "../posts/PostsSkeleton";

export default function PressReleaseSection() {
  const [pressReleases, setPressReleases] = useState([]);
  const [pressReleaseSettings, setPressReleaseSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch homepage settings for press release section
        const settingsResponse = await fetch(`${config.NEXT_PUBLIC_API_URL}/api/public/settings/homepage/press-release`);
        const settingsData = await settingsResponse.json();

        if (settingsData.success && settingsData.data) {
          setPressReleaseSettings(settingsData.data);

          // Only fetch press releases if section is enabled
          if (settingsData.data.enabled !== false) {
            const pressResponse = await fetch(
              `${config.NEXT_PUBLIC_API_URL}/api/public/p/press-release?limit=${settingsData.data.limit ? settingsData.data.limit : 6}`
            );

            if (pressResponse.ok) {
              const pressData = await pressResponse.json();
              if (pressData.success) {
                const pressReleases = pressData.data.posts ? pressData.data.posts : [];
                setPressReleases(pressReleases);
              }
            }
          }
        }
      } catch (error) {

      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [language]);

  const getLabel = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    if (typeof field === 'object') {
      return field[language] ? field[language] : (field.en ? field.en : '');
    }
    return '';
  };



  if (loading) {
    return (
      <section className="py-6 sm:py-8 md:py-10 lg:py-12 xl:py-14 px-2 sm:px-3 md:px-4 lg:px-6">
        <div className="max-w-7xl mx-auto w-full">
          <SectionHeader
            icon={Megaphone}
            sectionTitle="Press Releases"
            mainHeading="Latest Press Releases"
          />
          <PostsSkeleton />
        </div>
      </section>
    );
  }

  // Hide section if disabled or no meaningful content
  if (pressReleaseSettings.enabled === false) {
    return null;
  }
  if (!getLabel(pressReleaseSettings.sectionTitle) && !getLabel(pressReleaseSettings.sectionSubtitle) && pressReleases.length === 0) {
    return null;
  }

  return (
    <section className="py-6 sm:py-8 md:py-10 lg:py-12 xl:py-14 px-2 sm:px-3 md:px-4 lg:px-6" style={{ background: 'linear-gradient(135deg, var(--color-background) 0%, var(--color-muted) 100%)' }}>
      <div className="max-w-7xl mx-auto w-full">
        <SectionHeader
          icon={Megaphone}
          sectionTitle={pressReleaseSettings.sectionTitle}
          mainHeading={pressReleaseSettings.sectionSubtitle}
          description={pressReleaseSettings.description}
          buttonText={pressReleaseSettings.viewAllButtonText}
          buttonLink={pressReleases.length > 0 && pressReleaseSettings.showViewAllButton !== false ? (pressReleaseSettings.viewAllButtonLink || '/press-releases') : null}
          onButtonClick={pressReleases.length > 0 && pressReleaseSettings.showViewAllButton !== false ? () => {
            const link = pressReleaseSettings.viewAllButtonLink ? pressReleaseSettings.viewAllButtonLink : '/press-releases';
            const target = pressReleaseSettings.viewAllButtonTarget ? pressReleaseSettings.viewAllButtonTarget : '_self';
            if (target === '_blank') {
              window.open(link, '_blank');
            } else {
              window.location.href = link;
            }
          } : null}
          getLabel={getLabel}
        />

        {/* Press Releases Grid */}
        <div className="mobile-horizontal-scroll grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 lg:gap-6">
          {pressReleases.map((press, index) => (
            <PressReleaseCard key={press._id || press.id} pressRelease={press} />
          ))}
        </div>


      </div>
    </section>
  );
}